import { z } from 'zod';
import { daysBetweenInclusive } from '../dates';
import { id, ymd } from './common';

export const REPORT_GRANULARITIES = ['day', 'month', 'year'] as const;
export type ReportGranularity = (typeof REPORT_GRANULARITIES)[number];

/** Longest range per granularity (keeps tables and charts readable and fast). */
export const MAX_REPORT_DAYS: Record<ReportGranularity, number> = {
  day: 366,
  month: 1100,
  year: 3700,
};

const range = { startDate: ymd, endDate: ymd };
const ordered = (r: { startDate: string; endDate: string }) => r.startDate <= r.endDate;

/** GET /reports/pnl — profit & loss per day, month or year. */
export const pnlSeriesQuerySchema = z
  .object({
    ...range,
    granularity: z.enum(REPORT_GRANULARITIES).default('month'),
    yearMode: z.enum(['CALENDAR', 'FINANCIAL']).default('CALENDAR'),
    appId: id.optional(),
  })
  .refine(ordered, { path: ['endDate'], message: 'End date must be on or after start date' })
  .refine(
    (r) =>
      !ordered(r) || daysBetweenInclusive(r.startDate, r.endDate) <= MAX_REPORT_DAYS[r.granularity],
    {
      path: ['endDate'],
      message:
        'Range is too long for this view (daily ≤ 366 days, monthly ≤ 36 months, yearly ≤ 10 years)',
    },
  );

/** GET /reports/pnl-by-app — the same series, one row per app inside each bucket. */
export const pnlByAppQuerySchema = z
  .object({
    ...range,
    granularity: z.enum(REPORT_GRANULARITIES).default('month'),
    yearMode: z.enum(['CALENDAR', 'FINANCIAL']).default('CALENDAR'),
    /** Comma-separated app ids; empty = every app with data. */
    appIds: z
      .string()
      .optional()
      .transform((v) => (v ? v.split(',').filter(Boolean) : []))
      .pipe(z.array(id).max(50, 'Pick at most 50 apps')),
  })
  .refine(ordered, { path: ['endDate'], message: 'End date must be on or after start date' })
  .refine(
    (r) =>
      !ordered(r) || daysBetweenInclusive(r.startDate, r.endDate) <= MAX_REPORT_DAYS[r.granularity],
    {
      path: ['endDate'],
      message:
        'Range is too long for this view (daily ≤ 366 days, monthly ≤ 36 months, yearly ≤ 10 years)',
    },
  );

/** GET /reports/marketing */
export const marketingReportQuerySchema = z
  .object({ ...range, appId: id.optional() })
  .refine(ordered, { path: ['endDate'], message: 'End date must be on or after start date' })
  .refine((r) => !ordered(r) || daysBetweenInclusive(r.startDate, r.endDate) <= 1100, {
    path: ['endDate'],
    message: 'Range cannot exceed 1100 days',
  });
